import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { FadeUp, Counter } from "../components/Motion";
import LeadForm from "../components/LeadForm";
import { FAQSchema, BreadcrumbSchema } from "../components/SeoSchema";
import PageMeta from "../components/PageMeta";

const FAQS = (cityName) => [
  {
    q: `Do you work in ${cityName}?`,
    a: `Yes — ${cityName} is a core service area. We design, build, and maintain landscapes there year-round, with project management handled out of our Edmonton studio.`,
  },
  {
    q: `How long does a typical ${cityName} project take?`,
    a: `Most residential builds run 2–6 weeks depending on scope. Outdoor living projects with pavers, retaining walls, and lighting typically book 4–8 weeks in advance during peak season.`,
  },
  {
    q: `Are you licensed and insured in Alberta?`,
    a: `Yes. We carry full WCB coverage, $5M general liability, and partner only with certified hardscape and irrigation installers.`,
  },
  {
    q: `What does a project in ${cityName} typically cost?`,
    a: `Budgets vary by scope. Use our Estimator for a transparent range, or book a free on-site consultation and we'll provide a fixed proposal within five business days.`,
  },
];

export default function ServiceArea() {
  const { slug } = useParams();
  const [area, setArea] = useState(null);
  const [projects, setProjects] = useState([]);
  const [otherAreas, setOtherAreas] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get(`/service-areas/${slug}`).then((r) => setArea(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/service-areas").then((r) => setOtherAreas(r.data || []));
    api.get("/services").then((r) => setServices(r.data || []));
  }, [slug]);

  if (!area) return <div className="pt-32 px-6 text-[#D8D6D0]/40">Loading…</div>;

  const areaProjects = projects.filter((p) => (p.location || "").toLowerCase() === area.name.toLowerCase());
  const related = otherAreas.filter((a) => a.slug !== slug).slice(0, 4);
  const faqs = FAQS(area.name);
  const residentialServices = services.filter((s) => s.category === "residential").slice(0, 8);

  return (
    <div data-testid="service-area-page" className="pt-32 pb-32">
      <PageMeta
        title={`${area.name} Landscaping — Premium Outdoor Living`}
        description={`Premium landscape design, construction & outdoor living in ${area.name}, Alberta. ${area.projects_completed}+ projects delivered by NorthCrest Landscaping.`}
        path={`/areas/${slug}`}
      />
      <FAQSchema faqs={faqs} />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "https://northcrestlandscaping.ca/" },
          { name: "Service Areas", url: "https://northcrestlandscaping.ca/areas" },
          { name: area.name, url: `https://northcrestlandscaping.ca/areas/${slug}` },
        ]}
      />

      <section className="px-6 md:px-12 lg:px-16 mb-16">
        <FadeUp>
          <div className="flex items-center gap-3 mb-4">
            <div className="overline text-[#8C1D30]">Service Area</div>
            <span className="text-[#D8D6D0]/30">·</span>
            <Link to="/areas" data-testid="back-to-areas" className="overline text-[#D8D6D0]/60 hover:text-[#F5F3EF] link-underline">
              All Areas
            </Link>
          </div>
          <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-none max-w-5xl">
            Landscaping in <span className="italic text-[#D8D6D0]">{area.name}.</span>
          </h1>
          <p className="text-[#D8D6D0]/60 mt-8 max-w-2xl text-lg">{area.description}</p>
        </FadeUp>
      </section>

      <section className="px-6 md:px-12 lg:px-16 mb-16">
        <FadeUp>
          <div className="aspect-[16/8] overflow-hidden">
            <img src={area.image_url} alt={area.name} className="w-full h-full object-cover" />
          </div>
        </FadeUp>
      </section>

      <section className="px-6 md:px-12 lg:px-16 py-16 border-y border-[#D8D6D0]/10 grid grid-cols-2 md:grid-cols-3 gap-12">
        <FadeUp>
          <div className="font-serif-display text-5xl" data-testid="area-projects-count"><Counter value={area.projects_completed} suffix="+" /></div>
          <div className="overline text-[#D8D6D0]/50 mt-3">Projects in {area.name}</div>
        </FadeUp>
        <FadeUp delay={0.05}>
          <div className="font-serif-display text-5xl">6+</div>
          <div className="overline text-[#D8D6D0]/50 mt-3">Years Local Experience</div>
        </FadeUp>
        <FadeUp delay={0.1}>
          <div className="font-serif-display text-5xl">100%</div>
          <div className="overline text-[#D8D6D0]/50 mt-3">Licensed & Insured</div>
        </FadeUp>
      </section>

      {/* Local services */}
      <section className="px-6 md:px-12 lg:px-16 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <FadeUp>
          <h2 className="font-serif-display text-4xl md:text-5xl">What we build in {area.name}.</h2>
          <p className="text-[#D8D6D0]/60 mt-6 max-w-md">
            Climate-tested systems engineered for Alberta's freeze-thaw, designed with the rigour of architecture.
          </p>
        </FadeUp>
        <FadeUp delay={0.1}>
          <ul className="space-y-4">
            {[
              "Paver patios & walkways with engineered base systems",
              "Retaining walls — armour stone, allan block, custom concrete",
              "Outdoor kitchens, fire features & integrated lighting",
              "Full landscape design with 3D visualization",
              "Sod, irrigation, soft-scape & garden architecture",
              "Commercial maintenance contracts",
            ].map((s) => (
              <li key={s} className="flex items-start gap-3 text-[#D8D6D0]/80">
                <CheckCircle2 className="w-5 h-5 text-[#8C1D30] flex-shrink-0 mt-0.5" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </FadeUp>
      </section>

      {areaProjects.length > 0 && (
        <section className="px-6 md:px-12 lg:px-16 py-24 border-t border-[#D8D6D0]/10">
          <FadeUp>
            <h2 className="font-serif-display text-4xl md:text-5xl mb-12">Recent {area.name} projects.</h2>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {areaProjects.map((p) => (
              <FadeUp key={p.id}>
                <div className="group overflow-hidden">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={p.image_url} alt={p.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                  </div>
                  <div className="mt-4">
                    <div className="font-serif-display text-xl">{p.title}</div>
                    <div className="overline text-[#D8D6D0]/50 mt-1">{p.service_type}</div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="px-6 md:px-12 lg:px-16 py-24 border-t border-[#D8D6D0]/10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          <FadeUp>
            <div className="overline text-[#8C1D30] mb-3">FAQ</div>
            <h2 className="font-serif-display text-4xl md:text-5xl">{area.name} questions, answered.</h2>
          </FadeUp>
          <div className="lg:col-span-2 divide-y divide-[#D8D6D0]/10">
            {faqs.map((f, i) => (
              <FadeUp key={f.q} delay={i * 0.05}>
                <details className="group py-6" data-testid={`area-faq-${i}`}>
                  <summary className="cursor-pointer list-none flex justify-between items-start gap-6">
                    <span className="font-serif-display text-xl md:text-2xl text-[#F5F3EF]">{f.q}</span>
                    <span className="overline text-[#D8D6D0]/40 group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-[#D8D6D0]/70 max-w-3xl">{f.a}</p>
                </details>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Lead form */}
      <section className="px-6 md:px-12 lg:px-16 py-24 border-t border-[#D8D6D0]/10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <FadeUp>
            <h2 className="font-serif-display text-4xl md:text-5xl">Start your {area.name} project.</h2>
            <p className="text-[#D8D6D0]/60 mt-6 max-w-md">A senior designer will respond within one business day.</p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <LeadForm leadType="residential" source={`area-${slug}`} compact />
          </FadeUp>
        </div>
      </section>

      {/* Services in this area — deep links to AreaService combo pages */}
      {residentialServices.length > 0 && (
        <section className="px-6 md:px-12 lg:px-16 py-20 border-t border-[#D8D6D0]/10">
          <FadeUp>
            <div className="overline text-[#8C1D30] mb-3">Services in {area.name}</div>
            <h3 className="font-serif-display text-3xl md:text-4xl mb-8 max-w-3xl">
              Explore each build type with {area.name}-specific pricing, projects, and FAQ.
            </h3>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {residentialServices.map((s) => (
              <Link
                key={s.slug}
                to={`/areas/${slug}/${s.slug}`}
                data-testid={`area-service-link-${s.slug}`}
                className="group flex items-center justify-between px-5 py-4 border border-[#D8D6D0]/10 hover:border-[#F5F3EF]/40 hover:bg-[#F5F3EF]/5 transition-colors"
              >
                <span className="font-serif-display text-lg">
                  {s.name} <span className="text-[#D8D6D0]/40">in {area.name}</span>
                </span>
                <span className="text-[#8C1D30] group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Related areas */}
      {related.length > 0 && (
        <section className="px-6 md:px-12 lg:px-16 py-20 border-t border-[#D8D6D0]/10">
          <FadeUp>
            <div className="overline text-[#D8D6D0]/50 mb-6">Other Service Areas</div>
          </FadeUp>
          <div className="flex flex-wrap gap-3">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={`/areas/${a.slug}`}
                data-testid={`related-area-${a.slug}`}
                className="inline-flex items-center gap-2 px-4 py-3 border border-[#D8D6D0]/15 hover:border-[#F5F3EF]/40 hover:bg-[#F5F3EF]/5 transition-colors text-sm"
              >
                <MapPin className="w-3.5 h-3.5 text-[#8C1D30]" />
                <span>{a.name}</span>
              </Link>
            ))}
            <Link
              to="/areas"
              data-testid="view-all-areas"
              className="inline-flex items-center gap-2 px-4 py-3 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-sm"
            >
              View all areas →
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
